import { useMemo, useState, type MouseEvent } from "react";
import CloseIcon from "@mui/icons-material/Close";
import Delete from "@mui/icons-material/Delete";
import Edit from "@mui/icons-material/Edit";
import Map from "@mui/icons-material/Map";
import TableChart from "@mui/icons-material/TableChart";
import {
  Box,
  Button,
  CircularProgress,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  ListItemIcon,
  Menu,
  MenuItem,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from "@mui/material";
import { Formik } from "formik";
import { v4 } from "uuid";
import { number, object, string } from "yup";

import type {
  CreateLocationPrecisionOverrideRequestUnbranded,
  LocationPrecisionOverrideUnbranded,
} from "@thalamos/vision-common";

import api, { apiHooks } from "../../../api/index.js";
import { PopupDialog } from "../../Dialog/PopupDialog.js";
import { LocationPrecisionOverrideMap } from "./LocationPrecisionOverrideMap.js";

// Validation schema
const locationPrecisionOverrideSchema = object({
  name: string().required("Name is required").max(255),
  minLatitude: number().required("Min latitude is required").min(-90).max(90),
  maxLatitude: number()
    .required("Max latitude is required")
    .min(-90)
    .max(90)
    .test(
      "greater-than-min",
      "Max latitude must be greater than min latitude",
      function (value) {
        // Yup types `this.parent` — the object under validation — as `any`.
        const parent = this.parent as { minLatitude?: number };
        if (value === undefined || parent.minLatitude === undefined)
          return true;
        return value > parent.minLatitude;
      },
    ),
  minLongitude: number()
    .required("Min longitude is required")
    .min(-180)
    .max(180),
  maxLongitude: number()
    .required("Max longitude is required")
    .min(-180)
    .max(180)
    .test(
      "greater-than-min",
      "Max longitude must be greater than min longitude",
      function (value) {
        // Yup types `this.parent` — the object under validation — as `any`.
        const parent = this.parent as { minLongitude?: number };
        if (value === undefined || parent.minLongitude === undefined)
          return true;
        return value > parent.minLongitude;
      },
    ),
  precision: number()
    .required("Precision is required")
    .positive("Precision must be positive")
    .max(1, "Precision must be at most 1"),
});

// Delete Modal Component
function DeleteOverrideModal({
  open,
  onClose,
  override,
  onConfirm,
}: {
  open: boolean;
  onClose: () => void;
  override: LocationPrecisionOverrideUnbranded;
  onConfirm: () => Promise<void>;
}) {
  const [isDeleting, setIsDeleting] = useState(false);

  const handleDelete = () => {
    setIsDeleting(true);
    void onConfirm()
      .then(() => {
        onClose();
      })
      .finally(() => {
        setIsDeleting(false);
      });
  };

  return (
    <PopupDialog open={open} onClose={onClose}>
      <DialogTitle>
        Delete Location Precision Override
        <IconButton
          aria-label="close"
          onClick={onClose}
          sx={{
            position: "absolute",
            right: 8,
            top: 8,
            color: (theme) => theme.palette.grey[500],
          }}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent>
        <Typography>
          Are you sure you want to delete this location precision override?
        </Typography>
        <Typography sx={{ fontWeight: "bold", margin: "2em 0" }}>
          {override.name}
          <br />
          Bounds: ({override.bounds.minLatitude}, {override.bounds.minLongitude}
          ) to ({override.bounds.maxLatitude}, {override.bounds.maxLongitude})
          <br />
          Precision: {override.precision}
        </Typography>
      </DialogContent>
      <DialogActions sx={{ justifyContent: "space-between", marginTop: "1em" }}>
        <Button variant="outlined" onClick={onClose} disabled={isDeleting}>
          Cancel
        </Button>
        <Button
          color="error"
          variant="contained"
          onClick={handleDelete}
          disabled={isDeleting}
        >
          {isDeleting ? <CircularProgress size={20} /> : "Delete"}
        </Button>
      </DialogActions>
    </PopupDialog>
  );
}

// Create/Edit Modal Component
function CreateEditOverrideModal({
  open,
  onClose,
  override,
  onSuccess,
}: {
  open: boolean;
  onClose: () => void;
  override?: LocationPrecisionOverrideUnbranded;
  onSuccess: () => void;
}) {
  const isEdit = !!override;
  const [isSubmitting, setIsSubmitting] = useState(false);

  const initialValues = override
    ? {
        name: override.name,
        minLatitude: override.bounds.minLatitude,
        maxLatitude: override.bounds.maxLatitude,
        minLongitude: override.bounds.minLongitude,
        maxLongitude: override.bounds.maxLongitude,
        precision: override.precision,
      }
    : {
        name: "",
        minLatitude: 0,
        maxLatitude: 0,
        minLongitude: 0,
        maxLongitude: 0,
        precision: 0.01,
      };

  return (
    <PopupDialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>
        {isEdit ? "Edit" : "Create"} Location Precision Override
        <IconButton
          aria-label="close"
          onClick={onClose}
          sx={{
            position: "absolute",
            right: 8,
            top: 8,
            color: (theme) => theme.palette.grey[500],
          }}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <Formik
        initialValues={initialValues}
        validationSchema={locationPrecisionOverrideSchema}
        onSubmit={async (values) => {
          setIsSubmitting(true);
          try {
            const request: CreateLocationPrecisionOverrideRequestUnbranded = {
              name: values.name,
              bounds: {
                minLatitude: values.minLatitude,
                maxLatitude: values.maxLatitude,
                minLongitude: values.minLongitude,
                maxLongitude: values.maxLongitude,
              },
              precision: values.precision,
            };

            if (isEdit && override) {
              await api.locationPrecisionOverride.update(override.id, request);
            } else {
              await api.locationPrecisionOverride.create(v4(), request);
            }
            onSuccess();
            onClose();
          } finally {
            setIsSubmitting(false);
          }
        }}
      >
        {({
          values,
          errors,
          touched,
          handleChange,
          handleBlur,
          submitForm,
        }) => (
          <>
            <DialogContent>
              <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
                <TextField
                  fullWidth
                  label="Name"
                  name="name"
                  value={values.name}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  error={touched.name && !!errors.name}
                  helperText={touched.name && errors.name}
                />
                <Typography variant="subtitle2" sx={{ mt: 2 }}>
                  Bounding Box
                </Typography>
                <Box sx={{ display: "flex", gap: 2 }}>
                  <TextField
                    label="Min Latitude"
                    name="minLatitude"
                    type="number"
                    value={values.minLatitude}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    error={touched.minLatitude && !!errors.minLatitude}
                    helperText={touched.minLatitude && errors.minLatitude}
                    slotProps={{
                      htmlInput: { step: 0.01 },
                    }}
                  />
                  <TextField
                    label="Max Latitude"
                    name="maxLatitude"
                    type="number"
                    value={values.maxLatitude}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    error={touched.maxLatitude && !!errors.maxLatitude}
                    helperText={touched.maxLatitude && errors.maxLatitude}
                    slotProps={{
                      htmlInput: { step: 0.01 },
                    }}
                  />
                </Box>
                <Box sx={{ display: "flex", gap: 2 }}>
                  <TextField
                    label="Min Longitude"
                    name="minLongitude"
                    type="number"
                    value={values.minLongitude}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    error={touched.minLongitude && !!errors.minLongitude}
                    helperText={touched.minLongitude && errors.minLongitude}
                    slotProps={{
                      htmlInput: { step: 0.01 },
                    }}
                  />
                  <TextField
                    label="Max Longitude"
                    name="maxLongitude"
                    type="number"
                    value={values.maxLongitude}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    error={touched.maxLongitude && !!errors.maxLongitude}
                    helperText={touched.maxLongitude && errors.maxLongitude}
                    slotProps={{
                      htmlInput: { step: 0.01 },
                    }}
                  />
                </Box>
                <TextField
                  fullWidth
                  label="Precision"
                  name="precision"
                  type="number"
                  value={values.precision}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  error={touched.precision && !!errors.precision}
                  helperText={
                    (touched.precision && errors.precision) ||
                    "Smaller values = more precise location data (e.g., 0.01 ≈ 1km, 0.05 ≈ 5km)"
                  }
                  slotProps={{
                    htmlInput: { step: 0.001, min: 0.001, max: 1 },
                  }}
                />
              </Box>
            </DialogContent>
            <DialogActions>
              <Button
                variant="outlined"
                onClick={onClose}
                disabled={isSubmitting}
              >
                Cancel
              </Button>
              <Button
                variant="contained"
                onClick={() => void submitForm()}
                disabled={isSubmitting}
              >
                {isSubmitting ? (
                  <CircularProgress size={20} />
                ) : isEdit ? (
                  "Update"
                ) : (
                  "Create"
                )}
              </Button>
            </DialogActions>
          </>
        )}
      </Formik>
    </PopupDialog>
  );
}

// Table Row Component
function OverrideRow({
  override,
  onRefresh,
}: {
  override: LocationPrecisionOverrideUnbranded;
  onRefresh: () => void;
}) {
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const open = Boolean(anchorEl);

  const handleClick = (event: MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleDelete = async () => {
    await api.locationPrecisionOverride.delete(override.id);
    onRefresh();
  };

  return (
    <>
      <TableRow>
        <TableCell>{override.name}</TableCell>
        <TableCell>
          ({override.bounds.minLatitude.toFixed(2)},{" "}
          {override.bounds.minLongitude.toFixed(2)}) to (
          {override.bounds.maxLatitude.toFixed(2)},{" "}
          {override.bounds.maxLongitude.toFixed(2)})
        </TableCell>
        <TableCell>{override.precision}</TableCell>
        <TableCell>
          <Button variant="outlined" onClick={handleClick}>
            Actions
          </Button>
          <Menu
            anchorEl={anchorEl}
            open={open}
            onClose={handleClose}
            onClick={handleClose}
          >
            <MenuItem
              onClick={() => {
                setShowEditModal(true);
              }}
            >
              <ListItemIcon>
                <Edit fontSize="small" color="primary" />
              </ListItemIcon>
              Edit
            </MenuItem>
            <MenuItem
              onClick={() => {
                setShowDeleteModal(true);
              }}
            >
              <ListItemIcon>
                <Delete fontSize="small" color="error" />
              </ListItemIcon>
              Delete
            </MenuItem>
          </Menu>
        </TableCell>
      </TableRow>

      {showDeleteModal && (
        <DeleteOverrideModal
          open={showDeleteModal}
          onClose={() => {
            setShowDeleteModal(false);
          }}
          override={override}
          onConfirm={handleDelete}
        />
      )}

      {showEditModal && (
        <CreateEditOverrideModal
          open={showEditModal}
          onClose={() => {
            setShowEditModal(false);
          }}
          override={override}
          onSuccess={onRefresh}
        />
      )}
    </>
  );
}

// Main Management Component
export function LocationPrecisionOverrideManagement() {
  const [filterQuery, setFilterQuery] = useState("");
  const [page, setPage] = useState(0);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [viewMode, setViewMode] = useState<"table" | "map">("table");
  const pageSize = 10;

  // For table view, use paginated search
  const [{ data, loading }, refetch] =
    apiHooks.locationPrecisionOverride.search(
      filterQuery,
      pageSize,
      page * pageSize,
    );

  // For map view, fetch all overrides
  const [{ data: allData, loading: allLoading }, refetchAll] =
    apiHooks.locationPrecisionOverride.getAll();

  const numPages = data && data.count ? Math.ceil(data.count / pageSize) : 1;

  const overrides = useMemo(() => {
    return data?.results ?? [];
  }, [data]);

  const allOverrides = useMemo(() => {
    return allData ?? [];
  }, [allData]);

  const handleRefresh = () => {
    void refetch();
    void refetchAll();
  };

  const handleViewModeChange = (
    _event: MouseEvent<HTMLElement>,
    newMode: "table" | "map" | null,
  ) => {
    if (newMode !== null) {
      setViewMode(newMode);
    }
  };

  const handleCloseCreateModal = () => {
    setShowCreateModal(false);
  };

  return (
    <Box>
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          marginBottom: "1em",
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
          <ToggleButtonGroup
            value={viewMode}
            exclusive
            onChange={handleViewModeChange}
            size="small"
          >
            <ToggleButton value="table" aria-label="table view">
              <TableChart sx={{ mr: 0.5 }} fontSize="small" />
              Table
            </ToggleButton>
            <ToggleButton value="map" aria-label="map view">
              <Map sx={{ mr: 0.5 }} fontSize="small" />
              Map
            </ToggleButton>
          </ToggleButtonGroup>

          {viewMode === "table" && (
            <TextField
              placeholder="Filter by name..."
              value={filterQuery}
              onChange={(e) => {
                setFilterQuery(e.target.value);
                setPage(0);
              }}
              size="small"
              sx={{ width: 300 }}
            />
          )}
        </Box>
        <Button
          variant="contained"
          onClick={() => {
            setShowCreateModal(true);
          }}
        >
          Add Override
        </Button>
      </Box>
      {viewMode === "table" ? (
        // Table View
        loading ? (
          <Box
            sx={{ display: "flex", justifyContent: "center", padding: "2em" }}
          >
            <CircularProgress />
          </Box>
        ) : (
          <>
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ fontWeight: 700 }}>Name</TableCell>
                    <TableCell sx={{ fontWeight: 700 }}>Bounds</TableCell>
                    <TableCell sx={{ fontWeight: 700 }}>Precision</TableCell>
                    <TableCell sx={{ fontWeight: 700 }}>Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {overrides.map((override) => (
                    <OverrideRow
                      key={override.id}
                      override={override}
                      onRefresh={handleRefresh}
                    />
                  ))}
                  {overrides.length === 0 && (
                    <TableRow>
                      <TableCell colSpan={4} align="center">
                        No location precision overrides found
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </TableContainer>
            <Box
              sx={{
                display: "flex",
                justifyContent: "center",
                paddingTop: "2em",
              }}
            >
              <button
                disabled={page === 0}
                onClick={() => {
                  if (page > 0) setPage(page - 1);
                }}
              >
                &#60;
              </button>
              <Box sx={{ marginLeft: "1em", marginRight: "1em" }}>
                Page {page + 1} of {numPages}
              </Box>
              <button
                disabled={page >= numPages - 1}
                onClick={() => {
                  if (page < numPages - 1) setPage(page + 1);
                }}
              >
                &#62;
              </button>
            </Box>
          </>
        )
      ) : (
        // Map View
        <LocationPrecisionOverrideMap
          overrides={allOverrides}
          loading={allLoading}
        />
      )}
      {showCreateModal && (
        <CreateEditOverrideModal
          open={showCreateModal}
          onClose={handleCloseCreateModal}
          onSuccess={handleRefresh}
        />
      )}
    </Box>
  );
}
